// Energy shield bubble — ported from pmndrs' flow-shield demo
// (https://pmnd.rs/blog/creating-flow-shield/, MIT) and adapted:
// unit-radius sphere geometry (mesh.scale carries the size, so all shader
// math runs on the unit sphere and hit radii are in radians), no bottom
// fade, and compile-time quality tiers for weak devices.
//
// uReveal is inverted: 0 = fully materialized, 1 = fully dissolved.
import {
	AdditiveBlending,
	Color,
	FrontSide,
	Mesh,
	ShaderMaterial,
	SphereGeometry,
	Vector3,
} from 'three';
import { getGraphicsQuality } from './graphics-settings.js';

const HIT_FLARE_LINGER = 0.8; // seconds visible after the last hit finished
const COLLAPSE_DURATION = 0.9;
const MATERIALIZE_DURATION = 0.7;
const OPACITY_RAMP_SPEED = 6; // per second

const QUALITY_TIERS = {
	// full effect: hex grid + cell flicker + flow noise + noisy hit rings
	high: { maxHits: 6, flowNoise: true, cellFlash: true, hitNoise: true, segments: [48, 32] },
	// hex grid stays (game theme!) — drops the noise octaves and halves the hit buffer
	low: { maxHits: 3, flowNoise: false, cellFlash: false, hitNoise: false, segments: [24, 16] },
};

const vertexShader = /* glsl */ `
	varying vec3 vNormal;
	varying vec3 vViewDir;
	varying vec3 vObjPos;

	void main() {
		vObjPos = position;
		vNormal = normalize(normalMatrix * normal);
		vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
		vViewDir = normalize(-viewPosition.xyz);
		gl_Position = projectionMatrix * viewPosition;
	}
`;

function buildFragmentShader({ maxHits, flowNoise, cellFlash, hitNoise }) {
	return /* glsl */ `
	#define MAX_HITS ${maxHits}
	${flowNoise ? '#define FLOW_NOISE' : ''}
	${cellFlash ? '#define CELL_FLASH' : ''}
	${hitNoise ? '#define HIT_NOISE' : ''}

	uniform float uTime;
	uniform vec3  uColor;
	uniform float uLife;
	uniform float uHexScale;
	uniform float uEdgeWidth;
	uniform float uFresnelPower;
	uniform float uFresnelStrength;
	uniform float uOpacity;
	uniform float uReveal;
	uniform float uFlashSpeed;
	uniform float uFlashIntensity;
	uniform float uNoiseScale;
	uniform vec3  uNoiseEdgeColor;
	uniform float uNoiseEdgeWidth;
	uniform float uNoiseEdgeIntensity;
	uniform float uNoiseEdgeSmoothness;
	uniform float uHexOpacity;
	uniform float uFlowScale;
	uniform float uFlowSpeed;
	uniform float uFlowIntensity;
	uniform vec3  uHitPos[MAX_HITS];
	uniform float uHitTime[MAX_HITS];
	uniform float uHitRingSpeed;
	uniform float uHitRingWidth;
	uniform float uHitMaxRadius;
	uniform float uHitDuration;
	uniform float uHitIntensity;
	uniform float uHitImpactRadius;

	varying vec3 vNormal;
	varying vec3 vViewDir;
	varying vec3 vObjPos;

	// Simplex 3D noise (Ashima/McEwan, public domain)
	vec3 mod289v3(vec3 x){ return x - floor(x*(1./289.))*289.; }
	vec4 mod289v4(vec4 x){ return x - floor(x*(1./289.))*289.; }
	vec4 permute(vec4 x){ return mod289v4(((x*34.)+1.)*x); }
	vec4 taylorInvSqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314*r; }

	float snoise(vec3 v){
		const vec2 C = vec2(1./6., 1./3.);
		const vec4 D = vec4(0., 0.5, 1., 2.);
		vec3 i  = floor(v + dot(v, C.yyy));
		vec3 x0 = v - i + dot(i, C.xxx);
		vec3 g  = step(x0.yzx, x0.xyz);
		vec3 l  = 1. - g;
		vec3 i1 = min(g.xyz, l.zxy);
		vec3 i2 = max(g.xyz, l.zxy);
		vec3 x1 = x0 - i1 + C.xxx;
		vec3 x2 = x0 - i2 + C.yyy;
		vec3 x3 = x0 - D.yyy;
		i = mod289v3(i);
		vec4 p = permute(permute(permute(
			i.z+vec4(0.,i1.z,i2.z,1.))
		 +i.y+vec4(0.,i1.y,i2.y,1.))
		 +i.x+vec4(0.,i1.x,i2.x,1.));
		float n_ = 0.142857142857;
		vec3  ns = n_*D.wyz - D.xzx;
		vec4 j   = p - 49.*floor(p*ns.z*ns.z);
		vec4 x_  = floor(j*ns.z);
		vec4 y_  = floor(j - 7.*x_);
		vec4 x   = x_*ns.x + ns.yyyy;
		vec4 y   = y_*ns.x + ns.yyyy;
		vec4 h   = 1. - abs(x) - abs(y);
		vec4 b0  = vec4(x.xy, y.xy);
		vec4 b1  = vec4(x.zw, y.zw);
		vec4 s0  = floor(b0)*2.+1.;
		vec4 s1  = floor(b1)*2.+1.;
		vec4 sh  = -step(h, vec4(0.));
		vec4 a0  = b0.xzyw + s0.xzyw*sh.xxyy;
		vec4 a1  = b1.xzyw + s1.xzyw*sh.zzww;
		vec3 p0  = vec3(a0.xy, h.x);
		vec3 p1  = vec3(a0.zw, h.y);
		vec3 p2  = vec3(a1.xy, h.z);
		vec3 p3  = vec3(a1.zw, h.w);
		vec4 norm = taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
		p0*=norm.x; p1*=norm.y; p2*=norm.z; p3*=norm.w;
		vec4 m = max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.);
		m = m*m;
		return 42.*dot(m*m, vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
	}

	// uColor at full shield → red when drained
	vec3 lifeColor(float life){
		return mix(vec3(1.0, 0.08, 0.04), uColor, life);
	}

	float hexPattern(vec2 p){
		p *= uHexScale;
		const vec2 s = vec2(1., 1.7320508);
		vec4 hC = floor(vec4(p, p-vec2(0.5,1.))/s.xyxy) + 0.5;
		vec4 h  = vec4(p-hC.xy*s, p-(hC.zw+0.5)*s);
		vec2 cell = (dot(h.xy,h.xy) < dot(h.zw,h.zw)) ? h.xy : h.zw;
		cell = abs(cell);
		float d = max(dot(cell, s*0.5), cell.x);
		return smoothstep(0.5-uEdgeWidth, 0.5, d);
	}

	#ifdef CELL_FLASH
	vec2 hexCellId(vec2 p){
		p *= uHexScale;
		const vec2 s = vec2(1., 1.7320508);
		vec4 hC = floor(vec4(p, p-vec2(0.5,1.))/s.xyxy) + 0.5;
		vec4 h  = vec4(p-hC.xy*s, p-(hC.zw+0.5)*s);
		return (dot(h.xy,h.xy) < dot(h.zw,h.zw)) ? hC.xy : hC.zw+0.5;
	}

	float cellFlash(vec2 cellId){
		float rnd   = fract(sin(dot(cellId, vec2(127.1,311.7)))*43758.5453);
		float phase = rnd * 6.2831;
		float speed = 0.5 + rnd * 1.5;
		return smoothstep(0.6, 1.0, sin(uTime*uFlashSpeed*speed+phase)) * uFlashIntensity;
	}
	#endif

	void main(){
		// reveal/dissolve — uReveal 0 shows everything, 1 dissolves everything
		float noise = snoise(vObjPos * uNoiseScale) * 0.5 + 0.5;
		float revealMask = smoothstep(uReveal - uNoiseEdgeWidth, uReveal, noise);
		if (revealMask < 0.001) discard;

		float innerFade  = mix(0.98, 0.15, uNoiseEdgeSmoothness);
		float edgeLow    = smoothstep(uReveal-uNoiseEdgeWidth, uReveal-uNoiseEdgeWidth*innerFade, noise);
		float edgeHigh   = smoothstep(uReveal-uNoiseEdgeWidth*0.15, uReveal, noise);
		float revealEdge = edgeLow * (1.0 - edgeHigh);

		float fresnel = pow(1.0 - dot(vNormal, vViewDir), uFresnelPower) * uFresnelStrength;

		#ifdef FLOW_NOISE
		float t   = uTime * uFlowSpeed;
		float fn1 = snoise(vObjPos*uFlowScale + vec3(t, t*0.6, t*0.4));
		float fn2 = snoise(vObjPos*uFlowScale*2.1 + vec3(-t*0.5, t*0.9, t*0.3));
		float flowNoise = (fn1*0.6 + fn2*0.4)*0.5 + 0.5;
		#else
		float flowNoise = 0.5;
		#endif

		// hex grid: cube-face projection, faded near the 45° seams so no
		// ghost grid and no hard discontinuity is visible
		vec3 absN = abs(normalize(vObjPos));
		float dominance = max(absN.x, max(absN.y, absN.z));
		float hexFade   = smoothstep(0.65, 0.85, dominance);

		vec2 faceUV;
		if (absN.x >= absN.y && absN.x >= absN.z) {
			faceUV = vObjPos.yz;
		} else if (absN.y >= absN.z) {
			faceUV = vObjPos.xz;
		} else {
			faceUV = vObjPos.xy;
		}

		float hex = hexPattern(faceUV) * hexFade;
		#ifdef CELL_FLASH
		float flash = cellFlash(hexCellId(faceUV)) * hexFade;
		#else
		float flash = 0.0;
		#endif

		// hit ring buffer: expanding geodesic ring + hex highlight zone per hit
		vec3  normPos     = normalize(vObjPos);
		float ringContrib = 0.0;
		float hexHitBoost = 0.0;

		for (int i = 0; i < MAX_HITS; i++) {
			float ht      = uHitTime[i];
			float elapsed = uTime - ht;
			float isActive = step(0.0, ht)
			               * step(0.0, elapsed)
			               * step(elapsed, uHitDuration);

			// geodesic distance on the sphere surface (radians)
			float dist = acos(clamp(dot(normPos, normalize(uHitPos[i])), -1.0, 1.0));

			float ringR = min(elapsed * uHitRingSpeed, uHitMaxRadius);
			#ifdef HIT_NOISE
			float noiseD = snoise(normPos*5.0 + vec3(elapsed*2.0)) * 0.05;
			#else
			float noiseD = 0.0;
			#endif
			float ring       = smoothstep(uHitRingWidth, 0.0, abs(dist + noiseD - ringR));
			float fade       = 1.0 - smoothstep(uHitDuration*0.5, uHitDuration, elapsed);
			float radialFade = 1.0 - smoothstep(uHitMaxRadius*0.75, uHitMaxRadius, ringR);
			ringContrib     += ring * fade * radialFade * isActive;

			float zone     = smoothstep(uHitImpactRadius, 0.0, dist);
			float zoneFade = 1.0 - smoothstep(0.0, uHitDuration*0.35, elapsed);
			hexHitBoost   += zone * zoneFade * isActive;
		}

		ringContrib = min(ringContrib, 2.0);
		hexHitBoost = min(hexHitBoost, 1.0);

		vec3 lColor = lifeColor(uLife);

		float effectiveHexOpacity = uHexOpacity + hexHitBoost * uHitIntensity;
		float intensity = hex * effectiveHexOpacity * (0.3 + fresnel*0.7) + fresnel*0.4 + flash;

		vec3 shieldColor = lColor * intensity * 2.0;
		shieldColor += lColor * (flowNoise * fresnel * uFlowIntensity);
		shieldColor += lColor * ringContrib * uHitIntensity;

		vec3 edgeColor = mix(uNoiseEdgeColor, lColor, 1.0 - uLife);
		vec3 edgeGlow  = edgeColor * revealEdge * uNoiseEdgeIntensity;

		float alpha = clamp(intensity*uOpacity*revealMask + revealEdge*uNoiseEdgeIntensity, 0.0, 1.0);

		gl_FragColor = vec4(shieldColor + edgeGlow, alpha);
	}
	`;
}

function createUniforms(color) {
	const tier = QUALITY_TIERS[getGraphicsQuality()] || QUALITY_TIERS.high;
	return {
		uTime: { value: 0 },
		uColor: { value: new Color(color) },
		uLife: { value: 1.0 },
		uHexScale: { value: 5.0 },
		uEdgeWidth: { value: 0.06 },
		uFresnelPower: { value: 1.8 },
		uFresnelStrength: { value: 1.75 },
		uOpacity: { value: 0.76 },
		uReveal: { value: 0 },
		uFlashSpeed: { value: 0.6 },
		uFlashIntensity: { value: 0.11 },
		uNoiseScale: { value: 2.3 },
		uNoiseEdgeColor: { value: new Color(color) },
		uNoiseEdgeWidth: { value: 0.02 },
		uNoiseEdgeIntensity: { value: 10.0 },
		uNoiseEdgeSmoothness: { value: 0.5 },
		uHexOpacity: { value: 0.13 },
		uFlowScale: { value: 4.3 },
		uFlowSpeed: { value: 1.13 },
		uFlowIntensity: { value: 4 },
		uHitPos: { value: Array.from({ length: tier.maxHits }, () => new Vector3(0, 1, 0)) },
		uHitTime: { value: new Array(tier.maxHits).fill(-999) },
		uHitRingSpeed: { value: 1.75 },
		uHitRingWidth: { value: 0.12 },
		uHitMaxRadius: { value: 2.4 },
		uHitDuration: { value: 1.4 },
		uHitIntensity: { value: 4.1 },
		uHitImpactRadius: { value: 0.5 },
	};
}

let sharedGeometries = {};

function getSphereGeometry(tier) {
	const key = tier.segments.join('x');
	if (!sharedGeometries[key]) {
		// shared across all shields; a disposeHierarchy on a dying unit may
		// dispose it, but three re-uploads it on next use — harmless
		sharedGeometries[key] = new SphereGeometry(1, tier.segments[0], tier.segments[1]);
	}
	return sharedGeometries[key];
}

const _hitWorld = new Vector3();

export default class Shield {

	constructor({ radius = 0.8, color = 0x26aeff } = {}) {
		this.tier = QUALITY_TIERS[getGraphicsQuality()] || QUALITY_TIERS.high;
		this.material = new ShaderMaterial({
			uniforms: createUniforms(color),
			vertexShader,
			fragmentShader: buildFragmentShader(this.tier),
			transparent: true,
			depthWrite: false,
			side: FrontSide,
			blending: AdditiveBlending,
		});
		this.mesh = new Mesh(getSphereGeometry(this.tier), this.material);
		this.mesh.name = 'SHIELD';
		this.mesh.scale.setScalar(radius);
		this.mesh.visible = false;
		this.mesh.renderOrder = 800;
		this.mesh.frustumCulled = false; // parent unit meshes reparent between groups; stale bounds would blink

		// wall-clock seconds with a random phase so fleets don't flicker in sync
		this.timeOffset = Math.random() * 100;
		this.lastUpdate = this.time;
		this.opacityTarget = 0;
		this.baseOpacity = this.material.uniforms.uOpacity.value;
		this.material.uniforms.uOpacity.value = 0;
		this.hitIndex = 0;
		this.pendingHits = [];
		this.lastActivity = -Infinity;
		this.state = 'up'; // up | collapsing | down | materializing
		this.stateStart = 0;
	}

	get time() {
		return performance.now() / 1000 + this.timeOffset;
	}

	setColor(color) {
		this.material.uniforms.uColor.value.setHex(color);
		this.material.uniforms.uNoiseEdgeColor.value.setHex(color);
	}

	setRadius(radius) {
		this.mesh.scale.setScalar(radius);
	}

	setLife(ratio) {
		const life = Math.max(0, Math.min(1, ratio || 0));
		this.material.uniforms.uLife.value = life;
		if (life <= 0 && this.state === 'up') this.collapse();
		if (life > 0 && (this.state === 'down' || this.state === 'collapsing')) this.materialize();
	}

	// worldPoint: where the shot came FROM (attacker); the impact is where
	// the line from there enters the bubble. delay syncs with beam travel.
	registerHitFromWorld(worldPoint, delaySeconds = 0) {
		if (this.state === 'down' || this.state === 'collapsing') return;
		_hitWorld.copy(worldPoint);
		this.mesh.updateWorldMatrix(true, false);
		this.mesh.worldToLocal(_hitWorld);
		if (_hitWorld.lengthSq() === 0) _hitWorld.set(0, 1, 0);
		_hitWorld.normalize();
		this.pendingHits.push({ direction: _hitWorld.clone(), due: this.time + delaySeconds });
		this.lastActivity = this.time + delaySeconds;
	}

	collapse() {
		this.state = 'collapsing';
		this.stateStart = this.time;
		this.pendingHits.length = 0;
	}

	materialize() {
		this.state = 'materializing';
		this.stateStart = this.time;
		this.mesh.visible = true;
	}

	update() {
		const uniforms = this.material.uniforms;
		const now = this.time;
		const dt = Math.min(0.2, now - this.lastUpdate);
		this.lastUpdate = now;
		uniforms.uTime.value = now;

		// flush due hits into the ring buffer
		for (let i = this.pendingHits.length - 1; i >= 0; i--) {
			const hit = this.pendingHits[i];
			if (now < hit.due) continue;
			const slot = this.hitIndex % this.tier.maxHits;
			this.hitIndex++;
			uniforms.uHitPos.value[slot].copy(hit.direction);
			uniforms.uHitTime.value[slot] = now;
			this.pendingHits.splice(i, 1);
		}

		switch (this.state) {
			case 'collapsing': {
				const progress = Math.min(1, (now - this.stateStart) / COLLAPSE_DURATION);
				uniforms.uReveal.value = progress;
				if (progress >= 1) {
					this.state = 'down';
					this.mesh.visible = false;
				}
				break;
			}
			case 'materializing': {
				const progress = Math.min(1, (now - this.stateStart) / MATERIALIZE_DURATION);
				uniforms.uReveal.value = 1 - progress;
				this.lastActivity = now; // keep it shown while forming
				if (progress >= 1) this.state = 'up';
				break;
			}
			default:
				uniforms.uReveal.value = 0;
		}

		// on-hit visibility envelope: visible while hits are live, then fade out
		const busy = this.pendingHits.length > 0
			|| now < this.lastActivity + uniforms.uHitDuration.value + HIT_FLARE_LINGER
			|| this.state === 'materializing'
			|| this.state === 'collapsing';
		this.opacityTarget = busy && this.state !== 'down' ? this.baseOpacity : 0;

		const current = uniforms.uOpacity.value;
		const step = OPACITY_RAMP_SPEED * dt * this.baseOpacity;
		if (current < this.opacityTarget) {
			uniforms.uOpacity.value = Math.min(this.opacityTarget, current + step * 2);
			this.mesh.visible = true;
		} else if (current > this.opacityTarget) {
			uniforms.uOpacity.value = Math.max(this.opacityTarget, current - step);
			if (uniforms.uOpacity.value <= 0.001 && this.state !== 'collapsing') this.mesh.visible = false;
		}
	}

	dispose() {
		this.mesh.parent?.remove(this.mesh);
		this.material.dispose(); // geometry is shared ($persistant)
	}
}
